<template>
  <Transition name="fade">
    <div
      v-if="modelValue"
      class="fixed inset-0 z-[9998] bg-black/30 backdrop-blur-[1px]"
      @click.self="closeDrawer"
    />
  </Transition>

  <Transition name="slide">
    <aside
      v-if="modelValue"
      class="fixed right-0 top-0 z-[9999] h-screen w-full max-w-2xl overflow-y-auto bg-white shadow-2xl"
    >
      <div
        class="sticky top-0 z-10 border-b border-gray-100 bg-white/95 px-6 py-5 backdrop-blur"
      >
        <div class="flex items-start justify-between gap-4">
          <div>
            <p
              class="text-xs font-semibold uppercase tracking-[0.2em] text-[#2f8f2f]"
            >
              Relatório de visita técnica
            </p>

            <h2 class="mt-1 text-2xl font-bold text-gray-900">
              {{ isEdit ? "Editar relatório" : "Novo relatório" }}
            </h2>

            <p class="mt-1 text-sm text-gray-500">
              Faça o pré-cadastro do relatório. O corpo técnico será preenchido
              nas próximas etapas do módulo.
            </p>
          </div>

          <button
            type="button"
            class="rounded-xl p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
            @click="closeDrawer"
          >
            <PhX class="h-5 w-5" />
          </button>
        </div>
      </div>

      <form class="px-6 py-6" @submit.prevent="submitForm">
        <div class="rounded-2xl border border-green-100 bg-green-50/50 p-4">
          <div class="flex gap-3">
            <div
              class="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-[#2f8f2f] shadow-sm"
            >
              <PhInfo class="h-5 w-5" />
            </div>

            <div>
              <h3 class="text-sm font-semibold text-gray-800">
                Registro inicial do relatório
              </h3>

              <p class="mt-1 text-sm text-gray-500">
                Nesta etapa criamos o cabeçalho do relatório. Depois
                conectaremos o builder com fotos, observações, recomendações e
                layout personalizado.
              </p>
            </div>
          </div>
        </div>

        <div class="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2">
          <div class="md:col-span-2">
            <label class="mb-1.5 block text-sm font-semibold text-gray-700">
              Empresa visitada <span class="text-red-500">*</span>
            </label>

            <select
              v-model="form.report_visit_client_id"
              class="report-input"
              required
            >
              <option value="" disabled>Selecione uma empresa ativa</option>

              <option
                v-for="client in activeClients"
                :key="client.id"
                :value="client.id"
              >
                {{ getClientOptionLabel(client) }}
              </option>
            </select>

            <p v-if="!activeClients.length" class="mt-1 text-xs text-red-500">
              Nenhuma empresa ativa disponível. Cadastre ou reative uma empresa
              na aba Empresas.
            </p>
          </div>

          <div class="md:col-span-2">
            <label class="mb-1.5 block text-sm font-semibold text-gray-700">
              Layout do relatório
            </label>

            <select
              v-model="form.report_layout_id"
              class="report-input"
            >
              <option :value="null">
                Usar sem layout
              </option>

              <option
                v-for="layout in activeLayouts"
                :key="layout.id"
                :value="layout.id"
              >
                {{ layout.name }}{{ layout.is_default ? " — padrão" : "" }}
              </option>
            </select>

            <p class="mt-1 text-xs text-gray-400">
              O layout define capa, cabeçalho, rodapé, marca d’água e áreas dinâmicas do PDF.
            </p>
          </div>

          <div class="md:col-span-2">
            <label class="mb-1.5 block text-sm font-semibold text-gray-700">
              Título do relatório <span class="text-red-500">*</span>
            </label>

            <input
              v-model="form.title"
              type="text"
              class="report-input"
              placeholder="Ex: Relatório de Visita Técnica"
              required
            />
          </div>

          <div>
            <label class="mb-1.5 block text-sm font-semibold text-gray-700">
              Tipo de relatório <span class="text-red-500">*</span>
            </label>

            <select v-model="form.report_type" class="report-input" required>
              <option
                v-for="type in reportTypes"
                :key="type.value"
                :value="type.value"
              >
                {{ type.label }}
              </option>
            </select>
          </div>

          <div>
            <label class="mb-1.5 block text-sm font-semibold text-gray-700">
              Data da visita
            </label>

            <input v-model="form.visit_date" type="date" class="report-input" />
          </div>

          <div>
            <label class="mb-1.5 block text-sm font-semibold text-gray-700">
              Técnico responsável
            </label>

            <input
              v-model="form.technician_name"
              type="text"
              class="report-input"
              placeholder="Nome do técnico"
            />
          </div>

          <div>
            <label class="mb-1.5 block text-sm font-semibold text-gray-700">
              Função / cargo
            </label>

            <input
              v-model="form.technician_role"
              type="text"
              class="report-input"
              placeholder="Ex: Técnico de Segurança do Trabalho"
            />
          </div>
        </div>

        <div class="mt-6">
          <label class="mb-1.5 block text-sm font-semibold text-gray-700">
            Observações iniciais
          </label>

          <textarea
            v-model="form.notes"
            rows="5"
            class="report-input resize-none"
            placeholder="Descreva brevemente o objetivo da visita, escopo inicial ou observações importantes..."
          />
        </div>

        <div
          v-if="isEdit"
          class="mt-6 rounded-2xl border border-gray-100 bg-gray-50 p-4"
        >
          <div class="flex items-center justify-between gap-4 text-sm">
            <span class="text-gray-500">Status atual</span>
            <ReportStatusBadge :status="form.status" />
          </div>
        </div>

        <div
          class="sticky bottom-0 mt-8 flex items-center justify-end gap-3 border-t border-gray-100 bg-white py-5"
        >
          <button
            type="button"
            class="rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-600 transition hover:bg-gray-50"
            @click="closeDrawer"
          >
            Cancelar
          </button>

          <button
            type="submit"
            class="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#2f8f2f] to-[#5dbb45] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:shadow-md disabled:cursor-not-allowed disabled:opacity-70"
            :disabled="loading || !activeClients.length"
          >
            <PhCircleNotch v-if="loading" class="h-5 w-5 animate-spin" />
            <PhCheck v-else class="h-5 w-5" />

            {{ isEdit ? "Salvar alterações" : "Criar relatório" }}
          </button>
        </div>
      </form>
    </aside>
  </Transition>
</template>

<script setup>
import { computed, reactive, watch } from "vue";
import { PhCheck, PhCircleNotch, PhInfo, PhX } from "@phosphor-icons/vue";

import ReportStatusBadge from "./ReportStatusBadge.vue";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  report: {
    type: Object,
    default: null,
  },
  clients: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  layouts: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(["update:modelValue", "submit", "close"]);

const form = reactive(getDefaultForm());

const isEdit = computed(() => Boolean(form.id));

const activeClients = computed(() => {
  return props.clients.filter((client) => client.is_active);
});

const activeLayouts = computed(() => {
  return props.layouts.filter((layout) => layout.is_active);
});

const defaultLayout = computed(() => {
  return activeLayouts.value.find((layout) => layout.is_default);
});

const reportTypes = [
  {
    label: "Relatório fotográfico",
    value: "photographic",
  },
  {
    label: "Relatório descritivo",
    value: "descriptive",
  },
  {
    label: "Texto livre",
    value: "free_text",
  },
  {
    label: "Foto + texto",
    value: "photo_text",
  },
  {
    label: "Checklist",
    value: "checklist",
  },
  {
    label: "Plano de ação",
    value: "action_plan",
  },
  {
    label: "Auditoria",
    value: "audit",
  },
];

watch(
  () => props.modelValue,
  (isOpen) => {
    if (!isOpen) return;

    fillForm(props.report);
  }
);

watch(
  () => props.report,
  (report) => {
    if (props.modelValue) {
      fillForm(report);
    }
  }
);

function getDefaultForm() {
  return {
    id: null,
    report_visit_client_id: "",
    report_layout_id: null,
    title: "Relatório de Visita Técnica",
    report_type: "photographic",
    visit_date: "",
    technician_name: "",
    technician_role: "",
    notes: "",
    status: "draft",
  };
}

function fillForm(report = null) {
  Object.assign(form, getDefaultForm());

  if (!report) {
    if (defaultLayout.value) {
      form.report_layout_id = defaultLayout.value.id;
    }

    return;
  }

  Object.assign(form, {
    ...getDefaultForm(),
    ...report,
    report_visit_client_id: report.report_visit_client_id || "",
    report_layout_id: report.report_layout_id || defaultLayout.value?.id || null,
    visit_date: normalizeDate(report.visit_date),
  });
}

function submitForm() {
  emit("submit", {
    id: form.id,
    payload: {
      report_visit_client_id: form.report_visit_client_id,
      report_layout_id: form.report_layout_id,
      title: form.title?.trim(),
      report_type: form.report_type,
      visit_date: form.visit_date || null,
      technician_name: form.technician_name?.trim(),
      technician_role: form.technician_role?.trim(),
      notes: form.notes?.trim(),
    },
    isEdit: isEdit.value,
  });
}

function closeDrawer() {
  emit("update:modelValue", false);
  emit("close");
}

function normalizeDate(value) {
  if (!value) return "";

  if (typeof value === "string" && value.length >= 10) {
    return value.substring(0, 10);
  }

  return "";
}

function getClientOptionLabel(client) {
  const name = client.trade_name || client.corporate_name || "Empresa sem nome";
  const city =
    client.city && client.state ? ` - ${client.city}/${client.state}` : "";

  return `${name}${city}`;
}
</script>

<style scoped>
.report-input {
  @apply w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-700 shadow-sm outline-none transition placeholder:text-gray-300 focus:border-[#2f8f2f] focus:ring-2 focus:ring-green-100;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.25s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}
</style>
